import type React from "react";
import { FCI_ERROR } from "@searchui-searchforms/shared/fci";
import type { NameFieldGroup } from "@/entities/form-metadata";
import { ErrorBoundary } from "@/shared/components/ErrorBoundary";
import type { GridSpan } from "@/shared/components/Grid";
import { Grid } from "@/shared/components/Grid";
import { useErrorBoundaryLogger } from "@/shared/lib/fci";
import { NonSelfNameField } from "./NonSelfNameField";
import { SelfNameField } from "./SelfNameField";
import { QuickFill } from "./quick-fill";
import { useNameFieldFocusTracking } from "./useNameFieldFocusTracking";

interface NameGroupProps {
    fieldGroup: NameFieldGroup;
}

export const NameGroup: React.FC<NameGroupProps> = ({ fieldGroup }) => {
    const spanDesktop = (fieldGroup.fields.length <= 2 ? 6 : 4) as GridSpan;
    const getFocusTracking = useNameFieldFocusTracking();
    const logger = useErrorBoundaryLogger();

    // A non-Self name group (e.g. an Executor/Informant, relation: "Other1") represents a
    // named party other than the search subject — it has no dataModel slot of its own. See
    // SelfNameField/NonSelfNameField for what differs between the two. F97/TT-3954.
    const relation = fieldGroup.fields[0]?.relation;
    const isNonSelfRelation = !!relation && relation !== "Self";

    // QuickFill fills the Self name (`values.name`), so it only belongs on the Self group even
    // though `features.quickFill` is server-computed per name group from viewer state.
    const showQuickFill = !isNonSelfRelation && (fieldGroup.features?.quickFill ?? false);

    return (
        <>
            <Grid.Container>
                {fieldGroup.fields.map((field) => {
                    const { onFocus } = getFocusTracking(field.assertion);
                    return isNonSelfRelation ? (
                        <NonSelfNameField
                            key={field.assertion}
                            field={field}
                            relation={relation}
                            spanDesktop={spanDesktop}
                            onFocus={onFocus}
                        />
                    ) : (
                        <SelfNameField
                            key={field.assertion}
                            field={field}
                            spanDesktop={spanDesktop}
                            onFocus={onFocus}
                        />
                    );
                })}
            </Grid.Container>
            {showQuickFill && (
                <ErrorBoundary fallback={null} onError={logger(FCI_ERROR.Supplemental)}>
                    <QuickFill />
                </ErrorBoundary>
            )}
        </>
    );
};
